import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { HelpCircle, Settings2, FileEdit } from 'lucide-react';
import { getTotalWeeks } from '@/types/settings';
import type { DetailsTabProps } from './types';

export function DetailsTab({ options, onOptionsChange, setActiveTab }: Readonly<DetailsTabProps>): React.ReactElement {
  const handleChange = (section: keyof typeof options, field: string, value: any) => {
    onOptionsChange(section, field, value);
  };

  return (
    <div className="space-y-6">
      {/* General Settings Card */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            General Settings
          </CardTitle>
          <CardDescription>
            Configure basic asset management behaviors and defaults
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="assetAutoNumbering" className="text-base">
                    Asset Auto Numbering
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically generate asset numbers
                  </p>
                </div>
                <Switch
                  id="assetAutoNumbering"
                  checked={options.general.assetAutoNumbering}
                  onCheckedChange={(checked) =>
                    handleChange('general', 'assetAutoNumbering', checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="doNotAllowNegativeBoughtValue" className="text-base">
                    Prevent Negative Values
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Block negative bought values for assets
                  </p>
                </div>
                <Switch
                  id="doNotAllowNegativeBoughtValue"
                  checked={options.general.doNotAllowNegativeBoughtValue}
                  onCheckedChange={(checked) =>
                    handleChange('general', 'doNotAllowNegativeBoughtValue', checked)
                  }
                />
              </div>

              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="servicePeriodDefaults" className="text-base">
                    Service Period Defaults
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          Number of periods after the current period in which depreciation should start.
                          Leave zero/blank to depreciate in the current period.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="servicePeriodDefaults"
                  type="number"
                  min="0"
                  value={options.general.servicePeriodDefaults}
                  onChange={(e) =>
                    handleChange('general', 'servicePeriodDefaults', parseInt(e.target.value) ?? 0)
                  }
                  placeholder="0"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-3 rounded-lg border p-4">
              <Label htmlFor="defaultBaseValueOn" className="text-base">
                Default Base Value On
              </Label>
              <Select
                value={options.general.defaultBaseValueOn}
                onValueChange={(value: string) => handleChange('general', 'defaultBaseValueOn', value)}
              >
                <SelectTrigger id="defaultBaseValueOn">
                  <SelectValue placeholder="Select base value calculation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="remaining">Remaining Postings</SelectItem>
                  <SelectItem value="original">Original Postings</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Choose how base values are calculated
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Nominal Update Settings Card */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle>Nominal Update Settings</CardTitle>
          <CardDescription>
            Configure when to prompt for nominal updates and journal settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Prompt Before Period End */}
          <div className="space-y-4">
            <div>
              <h4 className="mb-1 font-medium">Prompt Nominal Update Before Period End</h4>
              <p className="text-sm text-muted-foreground">
                Select which transaction types should trigger a nominal update prompt
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { key: 'depreciation', label: 'Depreciation' },
                { key: 'addition', label: 'Addition' },
                { key: 'revaluation', label: 'Revaluation' },
                { key: 'disposal', label: 'Disposal' },
                { key: 'transfer', label: 'Transfer' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center space-x-2 rounded-lg border p-3">
                  <Checkbox
                    id={`prompt${key}`}
                    checked={options.promptNominalUpdate[key as keyof typeof options.promptNominalUpdate]}
                    onCheckedChange={(checked) =>
                      handleChange('promptNominalUpdate', key, checked)
                    }
                  />
                  <Label htmlFor={`prompt${key}`} className="cursor-pointer font-normal">
                    {label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Journal Settings */}
          <div className="space-y-4">
            <div>
              <h4 className="mb-1 font-medium">Journal Settings</h4>
              <p className="text-sm text-muted-foreground">
                Configure how nominal journals are generated
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="includeResourceOnNominalJournals" className="text-base">
                    Include Resource on Journals
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Add resource information to nominal journals
                  </p>
                </div>
                <Switch
                  id="includeResourceOnNominalJournals"
                  checked={options.updateNominal.includeResourceOnNominalJournals}
                  onCheckedChange={(checked) =>
                    handleChange('updateNominal', 'includeResourceOnNominalJournals', checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="summariseJournals" className="text-base">
                    Summarise Journals by Default
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Combine similar journal entries automatically
                  </p>
                </div>
                <Switch
                  id="summariseJournals"
                  checked={options.updateNominal.summariseJournals}
                  onCheckedChange={(checked) =>
                    handleChange('updateNominal', 'summariseJournals', checked)
                  }
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Settings Card */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle>Transaction Settings</CardTitle>
          <CardDescription>
            Configure transaction processing rules and constraints
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="allowZeroValueAdditions" className="text-base">
                Allow Zero Value Additions
              </Label>
              <p className="text-sm text-muted-foreground">
                Permit adding assets with zero initial value
              </p>
            </div>
            <Switch
              id="allowZeroValueAdditions"
              checked={options.transactions.allowZeroValueAdditions}
              onCheckedChange={(checked) =>
                handleChange('transactions', 'allowZeroValueAdditions', checked)
              }
            />
          </div>

          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="forceAllDepreciationBeforePeriodEnd" className="text-base">
                Force Depreciation Before Period End
              </Label>
              <p className="text-sm text-muted-foreground">
                Require all depreciation before closing periods
              </p>
            </div>
            <Switch
              id="forceAllDepreciationBeforePeriodEnd"
              checked={options.transactions.forceAllDepreciationBeforePeriodEnd}
              onCheckedChange={(checked) =>
                handleChange('transactions', 'forceAllDepreciationBeforePeriodEnd', checked)
              }
            />
          </div>

          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="doNotReverseInPeriodDepreciation" className="text-base">
                Preserve In-Period Depreciation
              </Label>
              <p className="text-sm text-muted-foreground">
                Don't reverse depreciation for backdated disposals
              </p>
            </div>
            <Switch
              id="doNotReverseInPeriodDepreciation"
              checked={options.transactions.doNotReverseInPeriodDepreciation}
              onCheckedChange={(checked) =>
                handleChange('transactions', 'doNotReverseInPeriodDepreciation', checked)
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Default Depreciation Settings Card */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle>Default Depreciation Settings</CardTitle>
          <CardDescription>
            Set default values for new asset depreciation calculations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
            <div className="space-y-0.5">
              <Label htmlFor="doNotDepreciate" className="text-base">
                Do Not Depreciate
              </Label>
              <p className="text-sm text-muted-foreground">
                Disable depreciation for new assets by default
              </p>
            </div>
            <Switch
              id="doNotDepreciate"
              checked={options.defaultSettings.doNotDepreciate}
              onCheckedChange={(checked) =>
                handleChange('defaultSettings', 'doNotDepreciate', checked)
              }
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="defaultMethod">
                  Default Method <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={options.defaultSettings.defaultMethod}
                  onValueChange={(value: string) => handleChange('defaultSettings', 'defaultMethod', value)}
                  disabled={options.defaultSettings.doNotDepreciate}
                >
                  <SelectTrigger id="defaultMethod">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Straight Line">Straight Line</SelectItem>
                    <SelectItem value="Reducing Balance">Reducing Balance</SelectItem>
                    <SelectItem value="Sum of Digits">Sum of Digits</SelectItem>
                    <SelectItem value="Period Ratio">Period Ratio</SelectItem>
                    <SelectItem value="None">None</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Depreciation calculation method for new assets
                </p>
                {/* Contextual display when Period Ratio is selected */}
                {options.defaultSettings.defaultMethod === 'Period Ratio' && (
                  <div className="mt-3 rounded-lg border bg-muted/50 p-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Period Pattern</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {options.periods.periodRatios.join(', ')} ({getTotalWeeks(options.periods.periodRatios)} weeks)
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setActiveTab('periods')}
                        className="text-xs"
                      >
                        <FileEdit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultPercentage">
                  Default Percentage <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="defaultPercentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={options.defaultSettings.defaultPercentage}
                    onChange={(e) =>
                      handleChange('defaultSettings', 'defaultPercentage', parseFloat(e.target.value) ?? 0)
                    }
                    disabled={options.defaultSettings.doNotDepreciate}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    %
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Annual depreciation rate percentage
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="defaultPeriodYears">
                  Default Period (Years) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="defaultPeriodYears"
                  type="number"
                  min="0"
                  value={options.defaultSettings.defaultPeriodYears}
                  onChange={(e) =>
                    handleChange('defaultSettings', 'defaultPeriodYears', parseInt(e.target.value) ?? 0)
                  }
                  disabled={options.defaultSettings.doNotDepreciate}
                />
                <p className="text-xs text-muted-foreground">
                  Expected useful life in years
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultFrequency">
                  Default Frequency <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={options.defaultSettings.defaultFrequency}
                  onValueChange={(value: string) => handleChange('defaultSettings', 'defaultFrequency', value)}
                  disabled={options.defaultSettings.doNotDepreciate}
                >
                  <SelectTrigger id="defaultFrequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem>
                    <SelectItem value="Half Yearly">Half Yearly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                    <SelectItem value="N/A">N/A</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  How often depreciation is calculated
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="forceTransfersOnChangingCategory" className="text-base">
                Force Transfers on Category Change
              </Label>
              <p className="text-sm text-muted-foreground">
                Require transfer transaction when changing asset category
              </p>
            </div>
            <Switch
              id="forceTransfersOnChangingCategory"
              checked={options.defaultSettings.forceTransfersOnChangingCategory}
              onCheckedChange={(checked) =>
                handleChange('defaultSettings', 'forceTransfersOnChangingCategory', checked)
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
